import React, { FunctionComponent, useEffect, useLayoutEffect, useMemo, useState } from 'react';

import { Button } from '@yandex-360/orb-components/dist/components/Button';
import { Textarea } from '@yandex-360/orb-components/dist/components/Textarea';
import { Textinput } from '@yandex-360/orb-components/dist/components/Textinput';
import { useSelector } from 'react-redux';

import { getAppLang } from '../../../../../../../../../utils/common-i18n';
import { i18n } from '../../../../../../../helpers/i18n';
import { useActions } from '../../../../../../../hooks';
import { sendSupportFeedbackAction, supportFormWasShownAction } from '../../../../../../settings';
import { SESSION_STORAGE_DESCRIPTION_KEY, SESSION_STORAGE_EMAIL_KEY } from '../../../../../../settings/constants';
import { getUserSelector } from '../../../../../../user';
import { EMAIL_REGEX } from '../../../../../constants';
import css from './styles.css';

export const FeedbackForm: FunctionComponent = () => {
    const { supportFormWasShown, sendSupportFeedback } = useActions({
        supportFormWasShown: supportFormWasShownAction,
        sendSupportFeedback: sendSupportFeedbackAction,
    });

    const user = useSelector(getUserSelector);

    const yandexDomain = getAppLang() === 'ru' ? 'yandex.ru' : 'yandex.com';
    const userEmail = user?.login?.includes?.('@') ? user.login : `${user?.login}@${yandexDomain}`;

    const [description, setDescription] = useState<string>('');
    const [email, setEmail] = useState<string>(user ? userEmail : '');
    const [hasEmailError, setHasEmailError] = useState<boolean>(false);

    const isSubmitButtonDisabled = useMemo(
        () => !description.trim().length || !email.trim().length,
        [email, description]
    );

    useLayoutEffect(() => {
        const email = sessionStorage.getItem(SESSION_STORAGE_EMAIL_KEY);
        const description = sessionStorage.getItem(SESSION_STORAGE_DESCRIPTION_KEY);
        if (email) {
            setEmail(email.toString());
        }
        if (description) {
            setDescription(description.toString());
        }
    }, []);

    useEffect(() => {
        supportFormWasShown();
    }, [supportFormWasShown]);

    const onEmailChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const value = event.target.value;
        setHasEmailError(false);
        setEmail(value);
        sessionStorage.setItem(SESSION_STORAGE_EMAIL_KEY, value);
    };

    const onDescriptionChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
        const value = event.target.value;
        setDescription(value);
        sessionStorage.setItem(SESSION_STORAGE_DESCRIPTION_KEY, value);
    };

    const onSubmit = (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        const isEmailValid = EMAIL_REGEX.test(email);
        if (isEmailValid) {
            sendSupportFeedback({ email, description });
        }
        setHasEmailError(!isEmailValid);
    };

    return (
        <form className={css.form} onSubmit={onSubmit}>
            <div className={css.fieldWrapper}>
                <Textinput
                    size="md"
                    name="email"
                    placeholder={i18n('settings-v2', 'feedback-form-mail-placeholder')}
                    value={email}
                    error={hasEmailError ? i18n('settings-v2', 'feedback-form-mail-error') : undefined}
                    onChange={onEmailChange}
                    testId="email-field"
                />
            </div>
            <div className={css.fieldWrapper}>
                <Textarea
                    name="description"
                    placeholder={i18n('settings-v2', 'feedback-form-description-placeholder')}
                    value={description}
                    maxLength={3000}
                    rows={4}
                    onChange={onDescriptionChange}
                    testId="description-field"
                />
            </div>
            <Button
                type="submit"
                className={css.submitButton}
                size="md"
                view="brand"
                isDisabled={isSubmitButtonDisabled}
                testId="submit-report-button"
            >
                {i18n('settings-v2', 'feedback-form-submit')}
            </Button>
        </form>
    );
};
